import React, { useEffect, useState } from 'react';

import { Box, Flex, Grid, GridItem, Spinner } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import {
  PageWrapper,
  SectionTitle,
  Text,
  WorldLocationsMap,
} from '../../../components';
import { getLocations } from '../../../data/mock.data.contact';
import { Location } from '../../../models/Location';
import { TextType } from '../../../models/Text';

import { EmailMeForm } from '.';

interface ContactSectionProps {
  marginTop?: number;
}

type LocationWithAddress = Location & {
  address?: string;
};

const ContactUsSection = ({ marginTop = 0 }: ContactSectionProps) => {
  const { t } = useTranslation();

  const [locations, setLocations] = useState<LocationWithAddress[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getLocations()
      .then((items: Location[]) => {
        setLocations(items as LocationWithAddress[]);
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load locations from Cockpit:', err);

        setLocations([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <PageWrapper>
      {/* CONTACT NAVIGATION ANCHOR */}
      <Box
        id="contact"
        width="100%"
        height="1px"
        scrollMarginTop="100px"
      />

      <Box paddingTop={`${marginTop}px`} />

      <SectionTitle
        title={t('homepage.contactUs.title')}
        description={t('homepage.contactUs.description')}
      />

      <Box
        width={{ base: '100%', md: '80%' }}
        marginY={{ base: '60px', md: '40px' }}
        paddingX={{ base: '20px', md: 0 }}
      >
        <WorldLocationsMap locations={locations} />
      </Box>

      {loading ? (
        <Flex
          width="100%"
          justifyContent="center"
        >
          <Spinner />
        </Flex>
      ) : null}

      {!loading && locations.length > 0 ? (
        <Grid
          width={{ base: '100%', md: '80%' }}
          templateColumns={{
            base: 'repeat(1, 1fr)',
            md: 'repeat(4, 1fr)',
          }}
          gap={{
            base: '40px',
            md: '25px',
          }}
        >
          {locations.map(
            (loc: LocationWithAddress, index: number) => (
              <GridItem
                key={`item-${index}-${loc.name}`}
                width="100%"
              >
                <Flex
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Text
                    type={{
                      base: TextType.Inter14Bold,
                      md: TextType.Inter18Bold,
                    }}
                    color="gray.500"
                    others={{
                      marginBottom: '20px',
                      textAlign: 'center',
                    }}
                  >
                    {(loc.name || '').toUpperCase()}
                  </Text>

                  {loc.contact?.phoneNumber ? (
                    <Text
                      type={{
                        base: TextType.Inter12Light,
                        md: TextType.Inter14Light,
                      }}
                      color="gray.500"
                      others={{
                        marginBottom: '10px',
                        textAlign: 'center',
                      }}
                    >
                      {loc.contact.phoneNumber}
                    </Text>
                  ) : null}

                  {loc.contact?.email ? (
                    <Text
                      type={{
                        base: TextType.Inter12Light,
                        md: TextType.Inter14Light,
                      }}
                      color="gray.500"
                      others={{
                        textAlign: 'center',
                        paddingX: '10px',
                      }}
                    >
                      {loc.contact.email}
                    </Text>
                  ) : null}

                  {loc.address ? (
                    <Text
                      type={{
                        base: TextType.Inter12Light,
                        md: TextType.Inter14Light,
                      }}
                      color="gray.500"
                      others={{
                        marginTop: '10px',
                        textAlign: 'center',
                        paddingX: '10px',
                      }}
                    >
                      {loc.address}
                    </Text>
                  ) : null}
                </Flex>
              </GridItem>
            )
          )}
        </Grid>
      ) : !loading ? (
        <Text>No locations available.</Text>
      ) : null}

      <EmailMeForm />
    </PageWrapper>
  );
};

export default ContactUsSection;